import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Badge,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { IconArrowRight, IconFlask, IconLayoutDashboard } from '@tabler/icons-react';
import { OBJECTIVES, kpisForObjective, STATUS_META, PILLARS, SCORECARD } from './kpiCatalog';

// Landing page for the 2026 objectives. Deliberately reads top-down: objective, then the KPIs that
// prove it. The exec team uses this as the table of contents while the KPI set is still in flux.

function KpiCard({ kpi }) {
  const status = STATUS_META[kpi.status] ?? STATUS_META.proposed;
  return (
    <UnstyledButton component={RouterLink} to={`/initiatives/kpi/${kpi.slug}`}>
      <Card withBorder radius="md" p="md" h="100%">
        <Stack gap="xs" h="100%" justify="space-between">
          <Stack gap={6}>
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Text fw={600}>{kpi.name}</Text>
              <Badge size="sm" color={status.color} variant="light">
                {status.label}
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              {kpi.tagline}
            </Text>
          </Stack>
          <Group gap={4} c="blue">
            <Text size="xs" fw={600}>
              Open KPI
            </Text>
            <IconArrowRight size={14} />
          </Group>
        </Stack>
      </Card>
    </UnstyledButton>
  );
}

export default function InitiativesOverview() {
  return (
    <Stack gap="xl" p="md">
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4}>
            <Title order={2}>2026 Initiatives</Title>
            <Text c="dimmed">
              The objectives the executive team is defining for 2026, and the KPIs that prove each
              one out.
            </Text>
          </Stack>
          <Badge color="grape" variant="light" leftSection={<IconFlask size={12} />}>
            Experimental
          </Badge>
        </Group>
      </Stack>

      <Card
        withBorder
        radius="md"
        p="lg"
        style={{ borderLeftWidth: 4, borderLeftStyle: 'solid', borderLeftColor: 'var(--mantine-color-grape-6)' }}
      >
        <Stack gap="md">
          <Stack gap={6}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700} lts={0.5}>
              Pillar {PILLARS[0]?.number}
            </Text>
            <Title order={3}>{PILLARS[0]?.name}</Title>
            <Text size="sm" c="dimmed">
              Everything below sits under this pillar — both objectives, both initiatives, and all
              nine KPIs. Pillars 1 and 2 are defined elsewhere and are not covered here.
            </Text>
          </Stack>

          {/* The scorecard is the answer to "just show me the numbers". The KPI cards below are
              the deep-dives behind it, so it goes first and is styled to be picked. */}
          <UnstyledButton component={RouterLink} to="/initiatives/scorecard">
            <Card withBorder radius="md" p="md" bg="var(--mantine-color-grape-light)">
              <Group justify="space-between" wrap="nowrap" align="center">
                <Group gap="sm" wrap="nowrap">
                  <IconLayoutDashboard size={22} />
                  <Stack gap={2}>
                    <Text fw={600}>{SCORECARD.name}</Text>
                    <Text size="sm" c="dimmed">
                      {SCORECARD.sections.join(' · ')} — every figure on one screen, for any date
                      range, with the managed-parts-sold detail exportable to CSV.
                    </Text>
                  </Stack>
                </Group>
                <IconArrowRight size={18} style={{ flexShrink: 0 }} />
              </Group>
            </Card>
          </UnstyledButton>
        </Stack>
      </Card>

      <Alert color="blue" variant="light" title="This is a working draft">
        The objective and KPI set below is a starting point, not a final list — definitions are
        still being negotiated. Every figure shown is real data from the warehouse, but several
        KPIs rest on definitions the exec team has not ratified yet, and two are partly blocked by
        gaps in the source data. Each page states plainly what it can and cannot measure, and what
        still needs a decision. The SQL behind every page is stored in the database and linked from
        the page itself, so it can be validated without reading any application code.
      </Alert>

      {OBJECTIVES.map((objective) => {
        const kpis = kpisForObjective(objective.id);
        return (
          <Stack key={objective.id} gap="sm">
            <Stack gap={4}>
              <Group gap="xs" align="center">
                <Badge size="lg" variant="filled">
                  Objective {objective.number}
                </Badge>
                <Title order={3}>{objective.title}</Title>
              </Group>
              <Text size="sm" c="dimmed" maw={900}>
                {objective.summary}
              </Text>
            </Stack>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
              {kpis.map((kpi) => (
                <KpiCard key={kpi.slug} kpi={kpi} />
              ))}
            </SimpleGrid>
          </Stack>
        );
      })}
    </Stack>
  );
}
